import { motion } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { useContent } from "../../context/ContentContext";
import Logo from "../Logo";

export default function Hero() {
  const { t, pick } = useLang();
  const { content } = useContent();
  if (!content) return null;

  const phone = content.contact?.phone || "540-314-1957";
  const title = pick(content.hero, "title");
  const lines = title.split("\n");

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
  };
  const line = {
    hidden: { y: "110%" },
    show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      {/* background */}
      <div className="absolute inset-0 tactical-grid-bg opacity-60" />
      <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-acid/10 blur-[140px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-acid/5 blur-[120px]" />

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* text */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-acid mb-6 flex items-center gap-3"
            data-testid="hero-kicker"
          >
            <span className="w-8 h-px bg-acid" /> {pick(content.hero, "kicker")}
          </motion.p>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display uppercase leading-[0.85] text-white text-[3.2rem] sm:text-7xl lg:text-8xl xl:text-[7.5rem]"
            data-testid="hero-title"
          >
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span variants={line} className="block">
                  {i === lines.length - 1 ? (
                    <>
                      <span className="text-acid">{l.split(" ")[0]}</span>{" "}
                      {l.split(" ").slice(1).join(" ")}
                    </>
                  ) : (
                    l
                  )}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-xl text-silver text-base md:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            {pick(content.hero, "subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="group bg-acid px-8 py-4 font-heading font-semibold uppercase tracking-wide text-obsidian hover:bg-acid-hover transition-colors"
              data-testid="hero-cta-btn"
            >
              {t("hero_cta")}
            </button>
            <a
              href={`tel:${phone.replace(/[^0-9]/g, "")}`}
              className="inline-flex items-center justify-center gap-2 border border-white/25 px-8 py-4 font-heading font-semibold uppercase tracking-wide text-white hover:border-acid hover:text-acid transition-colors"
              data-testid="hero-call-btn"
            >
              <Phone size={18} /> {phone}
            </a>
          </motion.div>
        </div>

        {/* dynamic logo */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative border border-white/10 bg-black/40 p-4 backdrop-blur-sm"
            >
              <div className="absolute -inset-px border border-acid/30 pointer-events-none" />
              <Logo size={340} className="max-w-full" />
              <div className="absolute -bottom-3 -right-3 w-6 h-6 border-r-2 border-b-2 border-acid" />
              <div className="absolute -top-3 -left-3 w-6 h-6 border-l-2 border-t-2 border-acid" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-silver"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t("scroll")}</span>
        <ArrowDown size={16} className="animate-bounce text-acid" />
      </motion.div>
    </section>
  );
}
